import { cartierDebugTable } from './Debug'
import { getElementDescription } from './Utility'

class Tracking {
	constructor() {
		if (!Tracking.instance) {
			Tracking.instance = this
			this.impressionObserver = null
			this.elToObserve = []
			this._initImpressionObserver()
		}
		return Tracking.instance
	}

	/**
	 * DOM event handler
	 *
	 * @param {Object} obj
	 * @param {String|Object} [obj.wrapper] Selector or dom node -> item with new eventListener
	 * @param {Array|Object} [obj.selector] Array of selectors or dom Node -> wrapper's children that trigger the event. If undefined the event is always triggered
	 * @param {String} [obj.event = 'click'] Event to be listen
	 * @param {String} obj.type Tracking type
	 * @param {Object} obj.data Tracking data
	 * @param {Bool} obj.disablePropagation Enable propagation on event listener
	 *
	 * @memberof Tracking
	 */
	domEvent(obj) {
		const { wrapper, selector, event, id, data, disablePropagation = false } = obj

		const _WRAPPER =
			typeof wrapper === 'string' ? document.querySelector(wrapper) : wrapper || document.body

		if (!_WRAPPER) return

		let _selectors

		if (selector) {
			_selectors = []
			if (Array.isArray(selector)) {
				selector.forEach((s) => {
					_selectors = _selectors.concat([..._WRAPPER.querySelectorAll(s)])
				})
			} else _selectors = [selector]
		}

		const _EVENT = event || 'click'

		_WRAPPER.addEventListener(
			_EVENT,
			(e) => {
				if (disablePropagation) e.stopPropagation()
				if (
					typeof _selectors === 'undefined' ||
					(_selectors.length !== 0 &&
						(_selectors.indexOf(e.target) !== -1 ||
							_selectors.map((sel) => sel.contains(e.target)).some((sel) => sel)))
				) {
					this.trackEvent({ data, id }, e.target)
				}
			},
			false
		)
	}

	/**
	 * Track event handler
	 *
	 * @param {Object} trackObj
	 * @param {Object} [obj.data] product data | custom data
	 * @param {String} [obj.type = 'enh_prod'] tracking type id
	 * @param {HTMLElement} [target] Target element passed as parameter of data function
	 *
	 * @memberof Tracking
	 */
	trackEvent(trackObj, target = null) {
		const trackObjData = trackObj.data
		if (!trackObjData) return

		const data = this._parseDataObject(trackObjData, target)
		const id = trackObj.id || 'enh_prod'
		const isProductEvent = id === 'enh_prod' || id === 'batchImpressions'

		cartierDebugTable(
			{
				Type: id,
				...data,
			},
			['value']
		)

		if (data.dataSt) {
			cartierDebugTable(
				{
					Type: id,
					...data.dataSt,
				},
				['value']
			)
		}

		if (typeof yTos.tracking.trackProductData === 'function' && isProductEvent) {
			yTos.tracking.trackProductData(data, id)
			return
		}

		if (id === 'enh_addcart') {
			yTos.tracking.trackAddItem(data)
			return
		}
		window.tc_events_19?.(null, id, data)
	}

	/**
	 * Track impression handler
	 *
	 * @param {Object} el dom Node -> Element that will be tracked once
	 * @param {String} id tracking type id
	 * @param {Object} data product data | custom data
	 *
	 * @memberof Tracking
	 */
	trackImpression(el, id, data) {
		const mode = typeof data === 'string' ? 'attribute' : 'custom'

		const elDataset = el.dataset

		if (elDataset.impression) return

		elDataset.impression = JSON.stringify({
			mode,
			id,
			data,
		})

		if (!this.impressionObserver) {
			this.elToObserve.push(el)
			return
		}

		this.impressionObserver.observe(el)
	}

	abortImpression(el) {
		el?.removeAttribute('data-impression')
		yTos.Cartier.tracking.impressionObserver?.unobserve(el)
	}

	/**
	 * @param {Object|String} data
	 * @param {HTMLElement} target
	 * @returns {Object}
	 */
	_parseDataObject(data, target) {
		if (typeof data === 'string') {
			return JSON.parse(data)
		}

		const dataParsed = { ...data }

		Object.keys(dataParsed).forEach((p) => {
			if (typeof dataParsed[p] === 'function') {
				dataParsed[p] = dataParsed[p](target)
			}
		})

		return dataParsed
	}

	/**
	 * Init intersection observer
	 *
	 * @memberof Tracking
	 */
	_initImpressionObserver() {
		if (!this.impressionObserver) {
			this.impressionObserver = new IntersectionObserver(this._resolveImpression, {
				rootMargin: '0px',
				threshold: [0.3, 0.6, 1],
			})

			if (this.elToObserve.length > 0) {
				for (let i = 0; i < this.elToObserve.length; i += 1) {
					this.impressionObserver.observe(this.elToObserve[i])
				}

				this.elToObserve = []
			}
		}
	}

	/**
	 * Resolve impression handler
	 * @param {Array} [entries] Array of products
	 *
	 * @memberof Tracking
	 */
	_resolveImpression(entries) {
		entries.forEach((entry) => {
			if (entry.intersectionRatio >= 0.5) {
				const EL = entry.target
				if (!EL.dataset.impression) return

				let obj
				const dataImpression = JSON.parse(EL.dataset.impression)

				if (dataImpression.mode === 'attribute') {
					if (EL.hasAttribute(dataImpression.data)) {
						obj = EL.getAttribute(dataImpression.data)
					} else {
						obj = EL.querySelector(`[${dataImpression.data}]`).getAttribute(
							dataImpression.data
						)
					}
				} else {
					obj = dataImpression.data ?? JSON.parse(EL.dataset.ytosTrackProductData)
				}

				yTos.Cartier.tracking.trackEvent({
					id: dataImpression.id,
					data: obj,
				})

				EL?.removeAttribute('data-impression')
				yTos.Cartier.tracking.impressionObserver.unobserve(entry.target)
			}
		})
	}

	/**
	 * Get Slot data for tracking
	 *
	 * @param {Object} slot dom Node -> Element to track
	 * @param {Object} wrapper dom Node -> Element's wrapper
	 * @param {Boolean} getLayoutFromSlider
	 * @returns {Object}
	 *
	 * @memberof Tracking
	 */
	getSlotTrackingData(slot, wrapper, getLayoutFromSlider = false) {
		const isSlotInsideAlgolia = slot.closest('.algolia-element')
		const slotData = {}
		let str1
		let str2

		if (yTos.navigation.Action === 'AlgoliaResult') {
			str1 = 'Search pages'
			str2 = 'Search pages'
		} else if (isSlotInsideAlgolia) {
			str1 = 'Instant search pages'
			str2 = 'Instant search pages'
		} else {
			str1 = window.tc_vars?.msn_cgGroup
			str2 = window.tc_vars?.emerch_cgGroup
		}

		const name =
			slot.childElementCount > 0
				? getElementDescription(
						slot.querySelector('.slot-element__title') ||
							slot.querySelector('.slot-element__link') ||
							slot.querySelector('.slot-searchresult-element__title') ||
							slot.querySelector('video-element') ||
							slot.querySelector('figure') ||
							slot.querySelector('.image-wrapper') ||
							slot.querySelector('p') ||
							slot
				  )
				: ''

		slotData.prmid = [str1, str2, name].filter(Boolean).join(' - ')
		slotData.prmname = name?.trim()
		slotData.prmcreative = getLayoutFromSlider
			? wrapper.querySelector('.product-carousel, .editorial-carousel, .slider-element')
					.dataset.layoutVariant
			: slot.dataset.slotLayout || wrapper.dataset.layoutVariant || 'default'
		slotData.prmposition =
			wrapper.dataset.elementIndex || wrapper.dataset.shelfRow || wrapper.dataset.position
		return slotData
	}
}

export default Tracking
